/**
 * The four sections of the screen, built from the ledger in the ledger's
 * order. Labels and order are `LEDGER_SECTIONS` in core; the section rules —
 * empty sections collapse, Probably done? shows at most five and counts the
 * rest — are applied here and nowhere else. Nothing is re-sorted: attention
 * (M2.7) ordered the items, and the screen shows them as ordered.
 */
import { LEDGER_SECTIONS } from '@cordelia/core'
import type { Ledger, LedgerItem, LedgerItemId, LedgerSectionId, Surface } from '@cordelia/core'
import { sectionOf } from '../../actions/catalog.js'
import type { LocalRouting } from '../../actions/routing.js'

/** A display cap: overflow stays in history with its rank (docs/semantics.md §5). */
export const PROBABLY_DONE_CAP = 5

const SURFACE: Readonly<Record<LedgerSectionId, Surface>> = {
  'you-owe': 'you-owe',
  'youre-waiting-on': 'waiting-on',
  'may-be-expected': 'may-be-expected',
  'probably-done': 'history',
}

export type LedgerSection = {
  readonly id: LedgerSectionId
  readonly label: string
  readonly surface: Surface
  /** The cards, in the ledger's order. */
  readonly items: readonly LedgerItem[]
  /**
   * Acted on this session and no longer a card here (M3.6b): marked done,
   * dropped, delegated or dismissed. It stays in the section it was acted on
   * in, as one line with the way back, so nothing vanishes on a press.
   */
  readonly settled: readonly LedgerItem[]
  /** Probably done? only: history items ranked past the cap. */
  readonly queued: number
}

/** The three sections a re-routed card can move to and still be a card. */
const ACTIVE: readonly LedgerSectionId[] = ['you-owe', 'youre-waiting-on', 'may-be-expected']

type Placement = { readonly section: LedgerSectionId | null; readonly settled: boolean }

/**
 * Where a card is shown, given the local overlay (M4.3 will replace it with a
 * real recompute). An overlay that lands on an active section moves the card
 * there; one that resolves, delegates or suppresses it leaves a settled line
 * where the press happened rather than making the card disappear.
 */
function placementOf(item: LedgerItem, routing: LocalRouting | undefined): Placement {
  const home = sectionOf(item.surface)
  if (routing === undefined) return { section: home, settled: false }
  const target = sectionOf(routing.surface)
  if (target !== null && ACTIVE.includes(target)) return { section: target, settled: false }
  return { section: home, settled: true }
}

export function sectionsOf(
  ledger: Ledger,
  cap = PROBABLY_DONE_CAP,
  routing?: ReadonlyMap<LedgerItemId, LocalRouting>,
): readonly LedgerSection[] {
  const placed = ledger.items.map((item) => ({
    item,
    ...placementOf(item, routing?.get(item.id)),
  }))
  return LEDGER_SECTIONS.map(({ id, label }) => {
    const mine = placed.filter((p) => p.section === id)
    const ranked = (i: LedgerItem): i is LedgerItem & { history_rank: number } =>
      i.history_rank !== null
    // Probably done? shows at most `cap`, by the rank attention gave it.
    const shown = (list: readonly LedgerItem[]): readonly LedgerItem[] =>
      id === 'probably-done' ? list.filter(ranked).filter((i) => i.history_rank < cap) : list
    const cards = mine.filter((p) => !p.settled).map((p) => p.item)
    const queued =
      id === 'probably-done' ? cards.filter(ranked).filter((i) => i.history_rank >= cap).length : 0
    return {
      id,
      label,
      surface: SURFACE[id],
      items: shown(cards),
      settled: shown(mine.filter((p) => p.settled).map((p) => p.item)),
      queued,
    }
  })
}

/** The DOM id of a card, so the skip link can reach it. */
export const cardDomId = (item: Pick<LedgerItem, 'id'>): string => `card-${item.id}`

/** The first card on the page in reading order; null when the ledger shows none. */
export function firstCardId(
  ledger: Ledger,
  cap = PROBABLY_DONE_CAP,
  routing?: ReadonlyMap<LedgerItemId, LocalRouting>,
): string | null {
  for (const section of sectionsOf(ledger, cap, routing)) {
    const first = section.items[0]
    if (first) return cardDomId(first)
  }
  return null
}
